"use client";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../../../../components/motionVariants";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import DocsPageFrame from "../../../components/DocsPageFrame";
import Callout from "../../../components/Callout";
import Screenshot from "../../../components/Screenshot";

const B = ({ children }) => (
  <span className="geist-medium text-[#111827]">{children}</span>
);

const RULES = [
  {
    topic: "Admin only",
    rule: "Settings → Organization → Currencies",
  },
  {
    topic: "Base currency cannot be deleted",
    rule: "The row tagged Base Currency has delete disabled. Edit still works (code stays locked). Same currency can be edited from Profile → Organization Preferences → gear",
  },
  {
    topic: "Currency Code",
    rule: "Required on create, stored uppercase. Locked after save — you cannot change INR to USD on an existing row",
  },
  {
    topic: "Exchange Rate",
    rule: "Shown and editable only for non-base currencies (rate against the base). Base row shows Base instead of a rate",
  },
  {
    topic: "Where the list is picked",
    rule: "Customer, Vendor, Bank, Price List, and document currency pickers. Customer, Vendor, and Bank currency cannot be changed after those records are saved",
  },
];

const sections = [
  {
    heading: "What this is for",
    subsections: [
      { heading: "1. The list" },
      { heading: "2. New Currency / Edit" },
    ],
  },
  { heading: "Business rules" },
];

const Definitions = ({ items }) => (
  <div className="space-y-2.5">
    {items.map((item) => (
      <p
        key={item.term}
        className="text-[16px] geist-regular text-[#374151] leading-relaxed"
      >
        <span className="geist-medium text-[#111827]">{item.term}:</span>{" "}
        {item.desc}
      </p>
    ))}
  </div>
);

const Steps = ({ items }) => (
  <ol className="space-y-3 mb-4">
    {items.map((text, i) => (
      <li key={i} className="flex items-start gap-3 list-none">
        <span className="w-6 h-6 rounded-full bg-[#111827] text-white text-[12px] geist-medium flex items-center justify-center flex-shrink-0 mt-0.5">
          {i + 1}
        </span>
        <p className="text-[16px] geist-regular text-[#374151] leading-relaxed">
          {text}
        </p>
      </li>
    ))}
  </ol>
);

const Flow = ({ steps }) => (
  <p className="text-[15px] geist-regular text-[#374151] leading-relaxed mb-4 px-4 py-3 bg-[#FAFAFA] border border-[#E5E5E5] rounded-[10px]">
    {steps.map((step, i) => (
      <span key={`${step}-${i}`}>
        {i > 0 && (
          <span className="text-[#9CA3AF] geist-medium"> {"→"} </span>
        )}
        <span className="geist-medium text-[#111827]">{step}</span>
      </span>
    ))}
  </p>
);

export default function CurrenciesPage() {
  return (
    <DocsPageFrame sections={sections}>
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.05 }}
        variants={stagger}
      >
        <motion.div variants={fadeUp} className="mb-10">
          <h1 className="text-[30px] leading-[1.2] text-[#111827] sentient-bold mb-4">
            Currencies
          </h1>
          <h2 className="text-[24px] sentient-medium text-[#111827] mb-3">
            For: Admins — the currencies the company can use, including the
            base
          </h2>
          <p className="text-[16px] sm:text-[17px] leading-[1.75] text-[#374151] geist-regular max-w-2xl">
            Title <B>Currencies</B>. Subtitle: Manage company currencies and
            base currency details. This list is what every currency picker in
            Books reads. Amounts on reports are shown in the base currency.
          </p>
          <Flow
            steps={["Settings", "Organization", "Currencies", "New Currency or Edit", "Save"]}
          />
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-0"
            className="text-[24px] sentient-medium text-[#111827] mb-6 scroll-mt-24"
          >
            What this is for
          </h2>

          <h3
            id="section-0-sub-0"
            className="text-[18px] sentient-medium text-[#111827] mb-3 scroll-mt-24"
          >
            1. The list
          </h3>
          <Definitions
            items={[
              {
                term: "Columns",
                desc: "Name (CODE - name, with a Base Currency tag on the base row), Symbol, Exchange Rate",
              },
              {
                term: "Actions",
                desc: "Edit. Delete with confirm Are you sure you want to delete \"CODE\"? — disabled on the base row",
              },
              {
                term: "What it affects",
                desc: "Which currencies you can put on a customer, vendor, bank, price list, or document. Base currency is how books and reports display amounts",
              },
            ]}
          />

          <h3
            id="section-0-sub-1"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            2. New Currency / Edit
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            <B>New Currency</B> opens Add new currency. Edit opens Edit
            currency. Same fields.
          </p>
          <Steps
            items={[
              <>
                Fill <B>Currency Code</B>, <B>Currency Symbol</B>,{" "}
                <B>Currency Name</B> (all required).
              </>,
              <>
                Set <B>Decimal Places</B> and <B>Format</B>. For a non-base
                currency, set <B>Exchange Rate</B> against the base.
              </>,
              <>
                Click <B>Save</B>. Editing the base also refreshes the company
                profile so the rest of the app picks up symbol and format.
              </>,
            ]}
          />
          <Callout type="note">
            You can also open the base currency editor from Profile →
            Organization Preferences → Base Currency gear. It is the same
            modal, not a second list.
          </Callout>
          <Screenshot caption="Currencies list with Base Currency tag; New Currency modal" />
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-1"
            className="text-[24px] sentient-medium text-[#111827] mb-4 scroll-mt-24"
          >
            Business rules
          </h2>
          <div className="border border-[#E5E5E5] rounded-[10px] overflow-hidden">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                  <th className="px-4 py-2.5 text-[13px] geist-medium text-[#111827] w-[28%]">
                    Topic
                  </th>
                  <th className="px-4 py-2.5 text-[13px] geist-medium text-[#111827]">
                    Rule
                  </th>
                </tr>
              </thead>
              <tbody>
                {RULES.map((row) => (
                  <tr
                    key={row.topic}
                    className="border-b border-[#E5E5E5] last:border-b-0"
                  >
                    <td className="px-4 py-2.5 text-[14px] geist-medium text-[#111827] align-top">
                      {row.topic}
                    </td>
                    <td className="px-4 py-2.5 text-[14px] geist-regular text-[#374151]">
                      {row.rule}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        <motion.div
          variants={fadeUp}
          className="flex flex-col sm:flex-row gap-4"
        >
          <Link
            href="/documentation/books/settings/profile"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Profile <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Base Currency also sits under Organization Preferences
            </p>
          </Link>
          <Link
            href="/documentation/books/banking/banks"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Banks <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Bank currency is picked from this list and then locked
            </p>
          </Link>
        </motion.div>
      </motion.div>
    </DocsPageFrame>
  );
}
